import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Custom components

// Helpers
import toastManager from "../../helpers/toastManager";

// Redux
import { useSelector } from "react-redux";
import { selectPatientId } from "../../redux/features/patientState/patientStateSlice";

// API
import useAxios from "../../hooks/useAxios";
import jsonFormatAuth from "../../apis/jsonFormatAuth";
import { Heading1 } from "../Headings/Headings";
import { ArrowPathIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { SquareIconNavButton, TextIconUtilityButton } from "../Buttons/Buttons";
import DataTable from "./DataTable";

function EntryDataTableTemplate({
  apiPath,
  newItemPath,
  dataTableName,
  showCreateEntryButton = true,
  isAllEntry = false,
}) {
  const patientId = useSelector(selectPatientId);
  const navigate = useNavigate();

  // Obtaining data
  const [entriesData, setEntriesData] = useState([]);
  const [patientTags, setPatientTags] = useState([]);

  // API Start
  const { response, error, axiosFetch } = useAxios();

  const getData = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: `${apiPath}${patientId}/`,
      sendSignal: false,
      requestConfig: {},
    });
  };

  useEffect(() => {
    // Obtaining data from API on initial page load
    getData();
  }, []);

  const handleCreateNewEntry = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "POST",
      url: `${apiPath}${patientId}/`,
      sendSignal: false,
      requestConfig: {},
    });
  };

  // RESPONSE Detection
  useEffect(() => {
    // 1: Custom action code handling.
    // Patients data received.
    if (response) {
      // console.log(response);
      if (response?.action_code === "entries_sent") {
        if (response?.payload) {
          const entriesDataResponse = response.payload?.entries;
          const patientTagsResponse = response.payload?.patient_tags;

          if (entriesDataResponse) {
            setEntriesData(entriesDataResponse);
          } else {
            setEntriesData([]);
          }

          if (patientTagsResponse) {
            setPatientTags(patientTagsResponse);
          } else {
            setPatientTags([]);
          }
        }
      }

      if (response?.action_code === "entry_created") {
        if (response?.payload) {
          const entryIdResponse = response.payload?.entry_id;
          if (entryIdResponse) {
            navigate(`${newItemPath}${entryIdResponse}/`);
            toastManager(
              "success",
              "Entry Created!",
              "Entry has been created successfully."
            );
          } else {
            toastManager(
              "error",
              "Issue with Entry Creation",
              "Cannot obtain entry_id from server."
            );
          }
        }
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // console.log(error);
      // 1: Custom action_code handling.
      // Patient does not exist, or does not belong to requesting user.
      if (error?.action_code === "patient_not_exist") {
        toastManager(
          "error",
          "Patient does not exist!",
          "Patient not found or not owned by the user."
        );
      }
    }
  }, [error]);

  // API End

  return (
    <div className="flex flex-col flex-grow items-center w-full pb-4 md:pb-0 Xbg-lime-500">
      <div className="flex flex-col md:flex-row items-center justify-between w-full Xbg-red-500">
        <div className="flex flex-row items-center justify-start w-full Xbg-green-500">
          <Heading1 headingText={dataTableName} />
        </div>
        <div className="flex flex-row space-x-4 items-center justify-end w-full h-full pt-5 pb-2 Xbg-yellow-500">
          <div className="flex-grow md:flex-grow-0">
            <SquareIconNavButton
              Icon={ArrowPathIcon}
              actionFunction={getData}
              activeColor={"active:bg-cyan-100"}
            />
          </div>
          {showCreateEntryButton && (
            <div className="flex-grow md:flex-grow-0">
              <TextIconUtilityButton
                Icon={PlusCircleIcon}
                prompt={"New Entry"}
                iconSide={"right"}
                activeColor={"active:bg-green-100"}
                actionFunction={handleCreateNewEntry}
              />
            </div>
          )}
        </div>
      </div>

      <div className="py-2" />

      {/* <div className="outline">{JSON.stringify(entriesData)}</div> */}

      <DataTable
        data={entriesData}
        dataTags={patientTags}
        rowType={isAllEntry ? "allEntry" : "entry"}
        showFilter={true}
        showPageNav={true}
      />
    </div>
  );
}

export default EntryDataTableTemplate;
